<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BedFrontSolid from '@/components/svg/BedFrontSolid.vue';
import LayerGroupSolid from '@/components/svg/LayerGroupSolid.vue';
import RulerCombinedSolid from '@/components/svg/RulerCombinedSolid.vue';
import TagSolid from '@/components/svg/TagSolid.vue';
import CircleInfoRegular from '@/components/svg/CircleInfoRegular.vue';
import DataTable from '@/components/tables/DataTable.vue';
import DataTableColumn from '@/components/tables/DataTableColumn.vue';
import DataTableStatusBadge from '@/components/tables/DataTableStatusBadge.vue';
import Tooltip from '@/components/utilities/Tooltip.vue';
import { useUnitBedroomDisplay } from '@/composables/useUnitBedroomDisplay';
import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper';
import { formatAvailabilityDate } from '@/helpers/dateHelpers';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Unit } from '@/typings/units/Unit.ts';
import { getUnitPositionText } from '@/enums/UnitPosition';
import { isUnitDisabled, UnitStatusEnum } from '@/enums/UnitStatus';

const { t, locale } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});
const { getBedroomCount } = useUnitBedroomDisplay();

const props = defineProps<{
  units: Unit[];
  filteredFloorId?: number;
  mode?: 'rental' | 'purchase';
}>();

const hoveredModel = defineModel<Unit | undefined>('hovered');

const emit = defineEmits<{
  'unit-click': [unit: Unit];
}>();

const filteredUnits = computed(() => {
  if (props.filteredFloorId) {
    return props.units.filter(unit => unit.floorId === props.filteredFloorId);
  }
  return props.units;
});

function getLivingAreaText(value?: number): string {
  if (!value || value <= 0) {
    return t('common.notAvailableAbbreviated');
  }

  return `${value} ${t('browsingApp.units.squareFeet')}`;
}

function handleRowClick(unit: Unit) {
  emit('unit-click', unit);
}

function getUnitConfigurationNames(unit: Unit): string {
  const names = getUnitConfigurationNamesArray(unit);
  return names.length > 0 ? names.join(', ') : t('common.notAvailableAbbreviated');
}

function getUnitConfigurationNamesArray(unit: Unit): string[] {
  const isEnglish = locale.value.toLowerCase().includes('en');

  if (!unit.unitUnitConfigurations || unit.unitUnitConfigurations.length === 0) {
    return [];
  }

  return unit.unitUnitConfigurations
    .map(config => {
      const name = isEnglish ? config.unitConfigurationNameEn : config.unitConfigurationName;
      return (name || '').trim();
    })
    .filter(name => name.length > 0);
}

function getUnitConfigurationCount(unit: Unit): number {
  return getUnitConfigurationNamesArray(unit).length;
}

function getUnitConfigurationTooltipText(unit: Unit): string {
  const names = getUnitConfigurationNamesArray(unit);
  return names.join('\n');
}

function getSizeCountLabel(count: number): string {
  return t('browsingApp.units.sizeChoices', { count }, count);
}

function getPositionText(unit: Unit): string {
  return getUnitPositionText(unit.position, t) || t('common.notAvailableAbbreviated');
}

function unitHasPromotion(unit: Unit): boolean {
  // Une unité louée ou vendue garde souvent le texte de promotion de son bâtiment : pas de badge dans ce cas
  if (unit.status === UnitStatusEnum.Rented || unit.status === UnitStatusEnum.Sold) {
    return false;
  }

  const localeValue = locale.value.toLowerCase();
 
  return Boolean((localeValue === 'en-ca' && (unit.promotionsEn?.trim()?.length ?? 0) > 0)
         || (localeValue === 'fr-ca' && (unit.promotions?.trim()?.length ?? 0) > 0));
}

function getPromotionText(unit: Unit): string {
  const localeValue = locale.value.toLowerCase();
  
  if (localeValue === 'en-ca') {
    return unit.promotionsEn?.trim() || '';
  }
  
  return unit.promotions?.trim() || '';
}
</script>

<template>
  <DataTable v-model:hovered="hoveredModel"
             class="dataTable_units"
             :items="filteredUnits"
             key-field="id"
             :row-class="''"
             @row-click="handleRowClick">
    <DataTableColumn field="unitNumber"
                     :label="t('browsingApp.units.number')"
                     class="unitNumberColumn">
      <template #body="{ value }">
        {{ `${t('browsingApp.units.unitNumber')} ${value}` }}
      </template>
    </DataTableColumn>
    <DataTableColumn field="localizedFloorName"
                     :label="t('browsingApp.units.floor')"
                     class="floorColumn">
      <template #labelIcon>
        <LayerGroupSolid class="dataTable-cardLabelIcon" />
      </template>
    </DataTableColumn>
    <DataTableColumn field="unitUnitConfigurations"
                     :label="t('browsingApp.units.size')"
                     class="sizesColumn">
      <template #body="{ item }: { item: Unit }">
        <template v-if="getUnitConfigurationCount(item) > 1">
          <Tooltip :text="getUnitConfigurationTooltipText(item)" position="top">
            <div class="sizesCount">
              <span class="sizesCount_label">{{ getSizeCountLabel(getUnitConfigurationCount(item)) }}</span>
              <CircleInfoRegular class="sizesCount_icon" />
            </div>
          </Tooltip>
        </template>
        <template v-else>
          {{ getUnitConfigurationNames(item) }}
        </template>
      </template>
    </DataTableColumn>
    <DataTableColumn field="position"
                     :label="t('browsingApp.units.position')"
                     class="positionColumn">
      <template #body="{ item }: { item: Unit }">
        {{ getPositionText(item) }}
      </template>
    </DataTableColumn>
    <DataTableColumn field="availabilityDate"
                     :label="props.mode === 'purchase' ? t('browsingApp.units.closestAvailabilityDate') : t('browsingApp.units.movingDate')"
                     class="availabilityDateColumn">
      <template #body="{ value, item }: { value: Date | undefined, item: Unit }">
        {{ formatAvailabilityDate(value, locale, t, props.mode, item.status) }}
      </template>
    </DataTableColumn>
    <!-- <DataTableColumn field="livingArea"
                     :label="t('browsingApp.units.livingArea')"
                     class="livingAreaColumn"
                     data-value-type="number">
      <template #labelIcon>
        <RulerCombinedSolid class="dataTable-cardLabelIcon" />
      </template>
      <template #body="{ value }: { value: number | undefined }">
        {{ getLivingAreaText(value) }}
      </template>
    </DataTableColumn> -->
    <DataTableColumn field="basePrice"
                     :label="props.mode === 'purchase' ? t('browsingApp.units.startingPrice') : t('browsingApp.units.pricePerMonth')"
                     class="basePriceColumn"
                     data-value-type="currency">
      <template #labelIcon>
        <TagSolid class="dataTable-cardLabelIcon" />
      </template>
      <template #body="{ value }: { value: number }">
        {{ value === null ? t('common.notAvailableAbbreviated') : formatCurrencyWithoutDecimal(value, locale) }}
      </template>
    </DataTableColumn>
    <DataTableColumn field="status"
                     :label="t('browsingApp.statuses.label')"
                     class="statusColumn">
      <template #body="{ value, item }">
        <div class="statusColumn_content">
          <DataTableStatusBadge class="statusBadge_unit"
                                :status="value"
                                :label="t(`browsingApp.statuses.unitStatus.${value}`)" />
          <Tooltip v-if="unitHasPromotion(item)"
                   :text="getPromotionText(item)"
                   position="top">
            <div class="tagPromotion">{{ t('unitSummary.promotion') }}</div>
          </Tooltip>
        </div>
      </template>
    </DataTableColumn>

    <template #empty>
      <span>
        {{ t('browsingApp.units.noUnitsAvailable') }}
      </span>
    </template>
  </DataTable>
</template>

<style lang="scss">
.dataTable-header,
.dataTable-cell {
  padding: .75rem 1.2rem;
}
.dataTable_units {

  .unitNumberColumn {
    width: 13%;
  }

  .bedroomsColumn {
    width: 15ch;
  }

  .floorColumn {
    width: 12%;
  }

  .sizesColumn {
    width: 13%;

    .sizesCount {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;

      .sizesCount_label {
        white-space: nowrap;
      }

      .sizesCount_icon {
        width: 1.4rem;
        height: 1.4rem;
        cursor: pointer;
        transition: color 0.2s ease;
        flex-shrink: 0;
      }
    }
  }

  .positionColumn {
    width: 10%;
  }

  .availabilityDateColumn {
    width: 17%;
  }

  .livingAreaColumn {
    width: 18%;
  }

  .basePriceColumn {
    width: 15%;
    text-align:left;
  }

  .statusColumn {
    width: 25%;
  }
  
  .dataTable-row--disabled {
    opacity: 0.6;
    background-color: var(--gray-50);
  }
}
.statusColumn_content{
    display:flex;
    gap: 1.0rem;
    align-items: center;
    flex-wrap: wrap;
}

@media screen and (max-width: 1400px){
  .basePriceColumn, .unitNumberColumn {
    font-weight: 600;
  }
  .dataTable_units {
    .sizesColumn {
      .sizesCount_icon {
        display: none;
      }
    }
  }
}


</style>
